import { type FormEvent, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { useAuth } from '@/hooks/useAuth';
import styles from './ForgotPasswordPage.module.css';

/**
 * Forgot Password page.
 *
 * Features:
 * - Email field with client-side format validation
 * - Success state showing confirmation message
 * - Loading state during API call
 * - Error display for failed requests
 * - "Voltar para login" link
 * - Focus management: auto-focus email field on mount
 */
export function ForgotPasswordPage() {
  const { forgotPassword, isLoading, error: authError, clearError } = useAuth();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  function handleEmailChange(value: string) {
    setEmail(value);
    if (formError) setFormError(null);
    if (authError) clearError();
    if (emailError) setEmailError('');
  }

  function validate(): boolean {
    if (!email.trim()) {
      setEmailError('E-mail é obrigatório.');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Formato de e-mail inválido.');
      return false;
    }
    return true;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    if (!validate()) {
      return;
    }

    try {
      await forgotPassword({ email: email.trim() });
      setIsSuccess(true);
    } catch {
      setFormError(authError ?? 'Erro ao enviar email. Tente novamente.');
    }
  }

  const displayError = formError ?? authError;

  if (isSuccess) {
    return (
      <AuthLayout>
        <div className={styles.card}>
          <div className={styles.successContainer} role="status">
            <span className={styles.successIcon} aria-hidden="true">
              <CheckCircle size={48} />
            </span>
            <h1 className={styles.successTitle}>Email enviado!</h1>
            <p className={styles.successMessage}>
              Verifique sua caixa de entrada. Enviamos um link de recuperação para{' '}
              <strong>{email}</strong>.
            </p>
            <p className={styles.successHint}>
              Não recebeu o email? Verifique a pasta de spam ou tente novamente.
            </p>
            <div className={styles.successActions}>
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={() => {
                  setIsSuccess(false);
                  setEmail('');
                }}
              >
                Enviar novamente
              </Button>
              <Link to="/login" className={styles.backLink}>
                <ArrowLeft size={16} aria-hidden="true" />
                Voltar para login
              </Link>
            </div>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className={styles.card}>
        <header className={styles.header}>
          <h1 className={styles.title}>Recuperar senha</h1>
          <p className={styles.subtitle}>
            Informe seu email e enviaremos um link para redefinir sua senha.
          </p>
        </header>

        {displayError && (
          <div className={styles.errorBanner} role="alert">
            <p>{displayError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className={styles.form}>
          <Input
            ref={emailRef}
            label="Email"
            type="email"
            name="email"
            placeholder="seu@email.com"
            autoComplete="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            error={emailError}
            leftIcon={<Mail size={18} />}
            fullWidth
            required
          />

          <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isLoading}>
            Enviar link de recuperação
          </Button>
        </form>

        <div className={styles.backLinkContainer}>
          <Link to="/login" className={styles.backLink}>
            <ArrowLeft size={16} aria-hidden="true" />
            Voltar para login
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}
